import { sessionLocationKindSchema } from "@/modules/classes/domain/contracts/sessions";
import { z } from "zod";
import {
  TemplateTierInputSchema,
  refinePrivateSharedTemplateTiers,
} from "../lib/template-tier";

export const privateLessonPackageKindSchema = z.enum([
  "individual",
  "group",
  "private_shared",
]);
export type PrivateLessonPackageKind = z.infer<
  typeof privateLessonPackageKindSchema
>;

export { TemplateTierInputSchema as PrivateLessonTemplateTierInputSchema };
export type PrivateLessonTemplateTierDTO = z.infer<
  typeof TemplateTierInputSchema
> & { id?: string };

const templateKindFields = {
  packageKind: privateLessonPackageKindSchema.default("individual"),
  groupSizeMin: z.number().int().positive().nullable().optional(),
  groupSizeMax: z.number().int().positive().nullable().optional(),
  flatPrice: z.number().int().positive().nullable().optional(),
  tiers: z.array(TemplateTierInputSchema).optional(),
};

function refineTemplateByPackageKind(
  data: {
    packageKind: PrivateLessonPackageKind;
    totalHours: number;
    pricePerHour: number;
    groupSizeMin?: number | null;
    groupSizeMax?: number | null;
    flatPrice?: number | null;
    tiers?: z.infer<typeof TemplateTierInputSchema>[];
  },
  ctx: z.RefinementCtx,
) {
  if (data.packageKind === "individual") {
    if (data.totalHours !== 1) {
      ctx.addIssue({
        code: "custom",
        message:
          "ແມ່ແບບສ່ວນຕົວຕ້ອງກຳນົດເປັນ 1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ",
        path: ["totalHours"],
      });
    }
    if (data.pricePerHour <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "ລາຄາຕໍ່ຊົ່ວໂມງຕ້ອງເປັນຈຳນວນບວກ",
        path: ["pricePerHour"],
      });
    }
    if (
      data.flatPrice != null ||
      data.groupSizeMin != null ||
      data.groupSizeMax != null ||
      (data.tiers != null && data.tiers.length > 0)
    ) {
      ctx.addIssue({
        code: "custom",
        message: "ແມ່ແບບສ່ວນຕົວບໍ່ໃຊ້ຟິລດແພັກເກດຫຼາຍຄົນ",
        path: ["packageKind"],
      });
    }
    return;
  }

  if (data.packageKind === "group") {
    if (data.totalHours < 1) {
      ctx.addIssue({
        code: "custom",
        message: "ແພັກເກດກຸ່ມຕ້ອງມີຊົ່ວໂມງຢ່າງໜ້ອຍ 1",
        path: ["totalHours"],
      });
    }
    if (data.flatPrice == null || data.flatPrice <= 0) {
      ctx.addIssue({
        code: "custom",
        message: "ຕ້ອງກຳນົດລາຄາຕໍ່ຄົນ",
        path: ["flatPrice"],
      });
    }
    if (data.groupSizeMin == null || data.groupSizeMax == null) {
      ctx.addIssue({
        code: "custom",
        message: "ຕ້ອງກຳນົດຈຳນວນຄົນຂັ້ນຕ່ຳ ແລະ ສູງສຸດ",
        path: ["groupSizeMin"],
      });
    } else if (data.groupSizeMin > data.groupSizeMax) {
      ctx.addIssue({
        code: "custom",
        message: "ຈຳນວນຄົນຂັ້ນຕ່ຳຕ້ອງບໍ່ເກີນສູງສຸດ",
        path: ["groupSizeMin"],
      });
    }
    if (data.tiers != null && data.tiers.length > 0) {
      ctx.addIssue({
        code: "custom",
        message: "ແມ່ແບບກຸ່ມບໍ່ໃຊ້ tier",
        path: ["tiers"],
      });
    }
    return;
  }

  // private_shared
  if (data.totalHours !== 1) {
    ctx.addIssue({
      code: "custom",
      message:
        "Private Package ຕ້ອງກຳນົດເປັນ 1 ຊົ່ວໂມງຕໍ່ຫນ່ວຍລາຄາ",
      path: ["totalHours"],
    });
  }
  if (data.pricePerHour !== 0) {
    ctx.addIssue({
      code: "custom",
      message: "Private Package ໃຊ້ tier ຄິດລາຄາ — pricePerHour = 0",
      path: ["pricePerHour"],
    });
  }
  if (
    data.flatPrice != null ||
    data.groupSizeMin != null ||
    data.groupSizeMax != null
  ) {
    ctx.addIssue({
      code: "custom",
      message: "Private Package ໃຊ້ tier ແທນ flatPrice/groupSize",
      path: ["packageKind"],
    });
  }
  refinePrivateSharedTemplateTiers(data.tiers ?? [], ctx);
}

/**
 * Private Lesson Template Schema - Template แพ็กเกจเรียนส่วนตัว / กลุ่ม / private shared
 */
export const PrivateLessonTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  packageKind: privateLessonPackageKindSchema,
  totalHours: z.number(),
  validDays: z.number(),
  pricePerHour: z.number(),
  groupSizeMin: z.number().nullable(),
  groupSizeMax: z.number().nullable(),
  flatPrice: z.number().nullable(),
  tiers: z
    .array(
      TemplateTierInputSchema.extend({
        id: z.string().optional(),
      }),
    )
    .optional(),
  defaultLocationKind: sessionLocationKindSchema,
  description: z.string().nullable(),
  status: z.enum(["active", "inactive"]),
  createdBy: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  packagesCount: z.number().optional(),
});
export type PrivateLessonTemplateDTO = z.infer<
  typeof PrivateLessonTemplateSchema
>;

/**
 * Create Private Lesson Template Schema
 */
export const CreatePrivateLessonTemplateSchema = z
  .object({
    name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ"),
    totalHours: z.number().int().positive("ຈຳນວນຊົ່ວໂມງຕ້ອງເປັນຈຳນວນບວກ"),
    validDays: z.number().int().positive("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນບວກ"),
    pricePerHour: z.number().int().min(0),
    defaultLocationKind: sessionLocationKindSchema.default("studio"),
    description: z.string().nullable().optional(),
    status: z.enum(["active", "inactive"]).default("active"),
    ...templateKindFields,
  })
  .superRefine(refineTemplateByPackageKind);
export type CreatePrivateLessonTemplateDTO = z.infer<
  typeof CreatePrivateLessonTemplateSchema
>;

/**
 * Update Private Lesson Template Schema
 */
export const UpdatePrivateLessonTemplateSchema = z
  .object({
    name: z.string().min(1, "ຊື່ຕ້ອງບໍ່ວ່າງ").optional(),
    totalHours: z.number().int().positive().optional(),
    validDays: z.number().int().positive("ຈຳນວນວັນຕ້ອງເປັນຈຳນວນບວກ").optional(),
    pricePerHour: z.number().int().min(0).optional(),
    defaultLocationKind: sessionLocationKindSchema.optional(),
    description: z.string().nullable().optional(),
    status: z.enum(["active", "inactive"]).optional(),
    packageKind: privateLessonPackageKindSchema.optional(),
    groupSizeMin: z.number().int().positive().nullable().optional(),
    groupSizeMax: z.number().int().positive().nullable().optional(),
    flatPrice: z.number().int().positive().nullable().optional(),
    tiers: z.array(TemplateTierInputSchema).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.packageKind == null && data.totalHours == null && !data.tiers) {
      return;
    }
    refineTemplateByPackageKind(
      {
        packageKind: data.packageKind ?? "individual",
        totalHours: data.totalHours ?? 1,
        pricePerHour: data.pricePerHour ?? 0,
        groupSizeMin: data.groupSizeMin,
        groupSizeMax: data.groupSizeMax,
        flatPrice: data.flatPrice,
        tiers: data.tiers,
      },
      ctx,
    );
  });
export type UpdatePrivateLessonTemplateDTO = z.infer<
  typeof UpdatePrivateLessonTemplateSchema
>;
